import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MailX } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { trpc } from "@/trpc";

// Surfaces the "you've unsubscribed from all email notifications" state on
// settings pages so a user who used the email "Unsubscribe from all" button
// can recover without finding the original email. Clicking the button
// deletes the global (NULL, EMAIL, NULL) tombstone; the banner disappears
// when the query refetches.
type Props = {
  // Query keys for the list endpoint(s) to invalidate on success so the
  // category cards reflect the new resolver state immediately. Optional —
  // pass whichever list query is rendered alongside the banner.
  invalidateOnSuccess?: ReadonlyArray<readonly unknown[]>;
};

export function GlobalEmailUnsubscribeBanner({ invalidateOnSuccess }: Props) {
  const queryClient = useQueryClient();
  const queryOptions = trpc.user.hasGlobalEmailUnsubscribe.queryOptions();
  const { data } = useQuery(queryOptions);

  const clearMutation = useMutation(
    trpc.user.clearGlobalEmailUnsubscribe.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(queryOptions),
          ...(invalidateOnSuccess ?? []).map((queryKey) =>
            queryClient.invalidateQueries({ queryKey }),
          ),
        ]);
        toast.success("You've been resubscribed to all emails.");
      },
      onError: () => {
        toast.error("Couldn't resubscribe. Try again.");
      },
    }),
  );

  if (!data?.hasGlobalOptOut) {
    return null;
  }

  return (
    <Alert className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-700">
      <MailX className="h-4 w-4" />
      <AlertTitle>You're unsubscribed from all emails</AlertTitle>
      <AlertDescription className="mt-1 flex flex-col gap-3">
        <span>
          Use the button below to resume, or enable specific categories one at a
          time.
        </span>
        <div>
          <Button
            type="button"
            variant="default"
            size="sm"
            disabled={clearMutation.isPending}
            onClick={() => clearMutation.mutate()}
          >
            {clearMutation.isPending
              ? "Resubscribing…"
              : "Resubscribe to all emails"}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
